import React, {useEffect, useState} from 'react';
import {useNavigate, useLocation} from "react-router-dom";
import api from "../../api";
import {Button, Col, Drawer, Form, Input, message, Row, Table, Popconfirm, Tabs} from "antd";
import {EditOutlined, DeleteOutlined, PlusOutlined, YoutubeOutlined} from '@ant-design/icons';

export default function AdminHelpVideos() {
    const navigate = useNavigate();
    const location = useLocation();
    const [videos, setVideos] = useState([]);
    const [loading, setLoading] = useState(false);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [editingVideo, setEditingVideo] = useState(null);
    const [form] = Form.useForm();

    // Determine active tab based on route
    const getActiveTab = () => {
        if (location.pathname === '/admin/suppliers') return 'suppliers';
        if (location.pathname === '/admin/icons') return 'icons';
        if (location.pathname === '/admin/users') return 'users';
        if (location.pathname === '/admin/blog') return 'blog';
        if (location.pathname === '/admin/ad-spends') return 'ad-spends';
        if (location.pathname === '/admin/affiliates') return 'affiliates';
        if (location.pathname === '/admin/help-videos') return 'help-videos';
        return 'stats';
    };

    const handleTabChange = (key) => {
        switch(key) {
            case 'stats':
                navigate('/admin');
                break;
            case 'suppliers':
                navigate('/admin/suppliers');
                break;
            case 'icons':
                navigate('/admin/icons');
                break;
            case 'users':
                navigate('/admin/users');
                break;
            case 'blog':
                navigate('/admin/blog');
                break;
            case 'ad-spends':
                navigate('/admin/ad-spends');
                break;
            case 'affiliates':
                navigate('/admin/affiliates');
                break;
            case 'help-videos':
                navigate('/admin/help-videos');
                break;
            default:
                navigate('/admin');
        }
    };

    useEffect(() => {
        loadVideos();
    }, []);

    const loadVideos = () => {
        setLoading(true);
        api.getHelpVideos()
            .then(setVideos)
            .finally(() => setLoading(false));
    };

    const handleCreate = () => {
        setEditingVideo(null);
        form.resetFields();
        setDrawerOpen(true);
    };

    const handleEdit = (video) => {
        setEditingVideo(video);
        form.setFieldsValue({
            youtube_video_id: video.youtube_video_id,
            language: video.language,
            pathname: video.pathname,
        });
        setDrawerOpen(true);
    };

    const handleDelete = (id) => {
        api.deleteHelpVideo(id)
            .then(() => {
                message.success('Help video verwijderd');
                loadVideos();
            })
            .catch(() => message.error('Fout bij verwijderen'));
    };

    const handleSubmit = () => {
        form.validateFields().then((values) => {
            const action = editingVideo
                ? api.updateHelpVideo(editingVideo.id, values)
                : api.createHelpVideo(values);

            action.then(() => {
                message.success(editingVideo ? 'Help video bijgewerkt' : 'Help video aangemaakt');
                setDrawerOpen(false);
                form.resetFields();
                loadVideos();
            }).catch(() => message.error('Fout bij opslaan'));
        });
    };

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 80,
        },
        {
            title: 'Pathname',
            dataIndex: 'pathname',
            key: 'pathname',
        },
        {
            title: 'Taal',
            dataIndex: 'language',
            key: 'language',
            width: 100,
        },
        {
            title: 'YouTube Video ID',
            dataIndex: 'youtube_video_id',
            key: 'youtube_video_id',
            render: (videoId) => (
                <a
                    href={`https://www.youtube.com/watch?v=${videoId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    <YoutubeOutlined /> {videoId}
                </a>
            ),
        },
        {
            title: 'Acties',
            key: 'actions',
            width: 200,
            render: (_, video) => (
                <Row gutter={8}>
                    <Col>
                        <Button
                            size="small"
                            icon={<EditOutlined />}
                            onClick={() => handleEdit(video)}
                        >
                            Bewerk
                        </Button>
                    </Col>
                    <Col>
                        <Popconfirm
                            title="Weet je zeker dat je deze help video wilt verwijderen?"
                            onConfirm={() => handleDelete(video.id)}
                            okText="Ja"
                            cancelText="Nee"
                        >
                            <Button
                                size="small"
                                danger
                                icon={<DeleteOutlined />}
                            >
                                Verwijder
                            </Button>
                        </Popconfirm>
                    </Col>
                </Row>
            ),
        },
    ];

    return (
        <>
            <h1>Admin</h1>

            <Tabs
                activeKey={getActiveTab()}
                onChange={handleTabChange}
                items={[
                    {
                        label: 'Stats',
                        key: 'stats',
                        children: null
                    },
                    {
                        label: 'Suppliers',
                        key: 'suppliers',
                        children: null
                    },
                    {
                        label: 'Icons',
                        key: 'icons',
                        children: null
                    },
                    {
                        label: 'Users',
                        key: 'users',
                        children: null
                    },
                    {
                        label: 'Blog Posts',
                        key: 'blog',
                        children: null
                    },
                    {
                        label: 'Ad Spends',
                        key: 'ad-spends',
                        children: null
                    },
                    {
                        label: 'Affiliates',
                        key: 'affiliates',
                        children: null
                    },
                    {
                        label: 'Help Videos',
                        key: 'help-videos',
                        children: (
                            <>
                                <Row justify="space-between" style={{marginBottom: '16px'}}>
                                    <Col>
                                        <h2>Help Videos</h2>
                                    </Col>
                                    <Col>
                                        <Button
                                            type="primary"
                                            icon={<PlusOutlined />}
                                            onClick={handleCreate}
                                        >
                                            Nieuwe Help Video
                                        </Button>
                                    </Col>
                                </Row>

                                <Table
                                    dataSource={videos}
                                    columns={columns}
                                    loading={loading}
                                    rowKey="id"
                                    pagination={{
                                        pageSize: 20,
                                        showSizeChanger: true,
                                        showTotal: (total) => `Totaal ${total} video's`,
                                    }}
                                />
                            </>
                        )
                    },
                ]}
            />

                <Drawer
                    title={editingVideo ? 'Help Video Bewerken' : 'Nieuwe Help Video'}
                    width={600}
                    onClose={() => setDrawerOpen(false)}
                    open={drawerOpen}
                    footer={
                        <Row justify="end" gutter={8}>
                            <Col>
                                <Button onClick={() => setDrawerOpen(false)}>
                                    Annuleren
                                </Button>
                            </Col>
                            <Col>
                                <Button type="primary" onClick={handleSubmit}>
                                    {editingVideo ? 'Bijwerken' : 'Aanmaken'}
                                </Button>
                            </Col>
                        </Row>
                    }
                >
                    <Form form={form} layout="vertical">
                        <Form.Item
                            name="pathname"
                            label="Pathname"
                            rules={[
                                {required: true, message: 'Vul een pathname in'},
                                {pattern: /^\//, message: 'Pathname moet beginnen met /'}
                            ]}
                            extra="Bijvoorbeeld: /gardens, /planner, /crops"
                        >
                            <Input placeholder="/gardens" />
                        </Form.Item>

                        <Form.Item
                            name="language"
                            label="Taal"
                            rules={[
                                {required: true, message: 'Vul een taal in'},
                                {len: 2, message: 'Taal moet 2 karakters zijn'}
                            ]}
                            extra="Twee-letter taalcode, bijvoorbeeld: en, nl"
                        >
                            <Input placeholder="nl" maxLength={2} />
                        </Form.Item>

                        <Form.Item
                            name="youtube_video_id"
                            label="YouTube Video ID"
                            rules={[{required: true, message: 'Vul een YouTube video ID in'}]}
                            extra="Het ID van de YouTube video (niet de volledige URL). Bijvoorbeeld: dQw4w9WgXcQ"
                        >
                            <Input placeholder="dQw4w9WgXcQ" />
                        </Form.Item>
                    </Form>
                </Drawer>
        </>
    );
}
